import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ChefHat, Clock, Heart, Users, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { useAction, submitBetaWaitlist } from 'wasp/client/operations';

interface BetaLandingPageProps {
  onComplete?: () => void;
}

export function BetaLandingPage({ onComplete }: BetaLandingPageProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState<number | null>(null);

  // submitBetaWaitlist is already imported as an action

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = (await submitBetaWaitlist({ email, name: name || undefined })) as any;
      if (result?.success) {
        setSubmitted(true);
        setPosition(result.position || null);
        setEmail('');
        setName('');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to join waitlist');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Lock className="w-8 h-8 text-warning" />,
      title: 'Founding Member Pricing',
      description: '$10/month locked in forever (future price: $25/month)',
    },
    {
      icon: <Clock className="w-8 h-8 text-warning" />,
      title: 'Shape the Product',
      description: 'Your feedback directly influences our roadmap and priorities',
    },
    {
      icon: <Heart className="w-8 h-8 text-warning" />,
      title: 'Early Access',
      description: 'Use cutting-edge features weeks before public launch',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand to-white">
      {/* Header */}
      <header className="border-b border-border/20 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold text-dusk">nura</div>
          <Badge className="bg-coral text-ink">Alpha Access</Badge>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-amber-100 text-amber-900 mb-4">Limited to 25 Founding Members</Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-dusk mb-6 leading-tight">
            Join us as a <br />
            <span className="text-warning">Founding Member</span>
          </h1>
          <p className="text-xl md:text-2xl text-supporting mb-8 max-w-2xl mx-auto leading-relaxed">
            Be one of the first 25 people to access nura in alpha. Shape the future of meal planning
            while paying just $10/month—locked in forever.
          </p>

          {/* Waitlist Form */}
          {!submitted ? (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8">
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border/30 focus:outline-none focus:ring-2 focus:ring-coral bg-white"
                />
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-border/30 focus:outline-none focus:ring-2 focus:ring-coral bg-white"
                />
                {error && (
                  <div className="text-sm text-destructive flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-coral desktop-hover:bg-coral/90 text-ink py-3 text-lg font-medium"
                >
                  {loading ? 'Joining...' : 'Get Founding Member Access'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="max-w-md mx-auto bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <div className="flex gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="font-semibold text-green-900 mb-1">You're on the waitlist! 🎉</h3>
                  <p className="text-sm text-green-800">
                    {position ? (
                      <>
                        You're spot <strong>#{position}</strong>. We'll email you within 24 hours with
                        access details.
                      </>
                    ) : (
                      <>We'll email you soon with access details!</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-supporting">
            No credit card required • Cancel anytime with full refund
          </p>
        </div>
      </section>

      {/* Alpha Disclaimer */}
      <section className="px-4 py-8 bg-amber-50 border-t border-b border-amber-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-2">Alpha Software — Here's What to Expect</h3>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>✓ Active development — expect weekly updates and improvements</li>
                <li>✓ Occasional bugs — we'll fix them fast with your help</li>
                <li>✓ New features rolling out constantly</li>
                <li>✓ Your feedback directly shapes what gets built next</li>
                <li>✓ <strong>No-questions-asked refunds</strong> if it doesn't work for you</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-16 bg-white/40">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-dusk mb-4">Founding Member Benefits</h2>
          <p className="text-xl text-supporting text-center mb-12 max-w-2xl mx-auto">
            Get exclusive perks by joining early
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white border-0 shadow-cardShadow rounded-[var(--radius-card)] p-8 text-center">
                <CardContent className="p-0">
                  <div className="mb-4 flex justify-center">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-dusk mb-3">{feature.title}</h3>
                  <p className="text-supporting leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-dusk">25</div>
              <div className="text-sm text-supporting mt-2">Founding members max</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-dusk">$10</div>
              <div className="text-sm text-supporting mt-2">Per month, locked forever</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-dusk">∞</div>
              <div className="text-sm text-supporting mt-2">Refund guarantee</div>
            </div>
          </div>

          <blockquote className="text-lg text-dusk italic mb-4 border-l-4 border-coral pl-6 py-2">
            "Actual nura user: This has completely changed how I meal plan. Every recipe fits exactly
            what I'm in the mood for."
          </blockquote>
          <cite className="text-supporting">— Early beta tester</cite>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16 bg-gradient-to-r from-coral/5 to-warning/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-dusk mb-4">Ready to Shape the Future?</h2>
          <p className="text-lg text-supporting mb-8">
            Only a few spots left. Join the founding members and influence product direction from day one.
          </p>

          {submitted ? (
            <Button
              disabled
              className="bg-gray-400 text-white cursor-default px-8 py-3 text-lg font-medium"
            >
              ✅ You're on the waitlist!
            </Button>
          ) : (
            <Button
              onClick={() => document.querySelector('input[type="email"]')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-coral desktop-hover:bg-coral/90 text-ink px-8 py-3 text-lg font-medium"
            >
              Join the Waitlist
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-2xl font-bold text-dusk mb-2">nura</div>
            <p className="text-supporting">Meal planning that actually works</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left mb-8">
            <div>
              <h4 className="font-semibold text-dusk mb-3">Alpha Program</h4>
              <ul className="space-y-2 text-supporting text-sm">
                <li>
                  <a href="#" className="desktop-hover:text-ink transition-colors">
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="#" className="desktop-hover:text-ink transition-colors">
                    Support
                  </a>
                </li>
                <li>
                  <a href="#" className="desktop-hover:text-ink transition-colors">
                    Feedback
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-dusk mb-3">Company</h4>
              <ul className="space-y-2 text-supporting text-sm">
                <li>
                  <a href="#" className="desktop-hover:text-ink transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <a href="#" className="desktop-hover:text-ink transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-dusk mb-3">Legal</h4>
              <ul className="space-y-2 text-supporting text-sm">
                <li>
                  <a href="/legal/privacy" className="desktop-hover:text-ink transition-colors">
                    Privacy
                  </a>
                </li>
                <li>
                  <a href="/legal/terms" className="desktop-hover:text-ink transition-colors">
                    Terms
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/20 pt-8 text-center text-supporting text-sm">
            <p>&copy; 2024 nura. Made with care for home cooks everywhere.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
